import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Move focus to the first field a failed submit rejected.
 *
 * WCAG expects a form that refuses to submit to take the visitor to the problem
 * rather than leaving them to hunt for it — particularly on the long gallery
 * form, where the first error can sit well above the button they just pressed.
 *
 * Keyed on an attempt counter rather than the error object so that submitting
 * twice with the same errors still re-focuses.
 */
export function useInvalidFieldFocus(): {
  formRef: React.RefObject<HTMLFormElement>;
  focusFirstInvalid: () => void;
} {
  const formRef = useRef<HTMLFormElement>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (attempt === 0) return;
    formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [attempt]);

  const focusFirstInvalid = useCallback(() => setAttempt((count) => count + 1), []);

  return { formRef, focusFirstInvalid };
}
